/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type ForwardRefExoticComponent, type Ref, type RefAttributes } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { RovoAnimatedLogo } from './components/RovoAnimatedLogo';
import { RovoColorLogo } from './components/RovoColorLogo';
import messages from './messages';
import type { AIButtonProps } from './types';

const atlaskitCssStyles = cssMap({
	pressableStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: '48px',
		width: '48px',
		backgroundColor: 'transparent',
		borderRadius: token('radius.xxlarge'),
	},
	boxStyles: {
		width: '48px',
		height: '48px',
		flexShrink: 0,
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error - TS2322 - specific box shadow for rovo button treatment
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
		transition: 'transform 0.2s ease-in-out',
		willChange: 'transform',

		'&:hover': {
			transform: 'scale(1.125)',
			transition: 'transform 0.1s ease-in-out',
			cursor: 'pointer',
		},
		'&:active': {
			transform: 'scale(1.0625)',
		},
	},
	boxStyleNoHovers: {
		width: '48px',
		height: '48px',
		flexShrink: 0,
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		transition: 'transform 0.2s ease-in-out',
		willChange: 'transform',
	},
	logoWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	unreadDot: {
		position: 'absolute',
		// @ts-expect-error - TS2322 - specific placement of indicator circle
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		top: '0.625rem',
		right: token('space.150'),
		width: '0.625rem',
		height: '0.625rem',
		borderRadius: token('radius.full'),
		// @ts-expect-error - TS2322 - specific color of indicator circle
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: token('color.border.information'),
		// @ts-expect-error - TS2322 - specific color of indicator circle
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		border: `1px solid ${token('color.background.neutral.bold')}`,
		pointerEvents: 'none',
	},
	// Awaiting-input indicator (TREX-1891): the ADS status-information icon
	// rendered in place of the plain unread dot.
	awaitingInputIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'absolute',
		// @ts-expect-error - TS2322 - specific placement of the indicator
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		top: '0.515rem',
		// @ts-expect-error - TS2322 - specific placement of the indicator
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		right: '0.625rem',
		width: 'auto',
		height: 'auto',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error - TS2322 - specific color of indicator circle
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		border: `1px solid ${token('color.background.neutral.bold')}`,
		pointerEvents: 'none',
	},
});

export const AIButton: ForwardRefExoticComponent<
	Omit<AIButtonProps, 'ref'> & RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, AIButtonProps>(
	(
		{
			onClick,
			isDisabled,
			onMouseEnter,
			onMouseLeave,
			hasUnread,
			isAwaitingInput,
			proximityAnimationKey,
			isExpandable,
		},
		ref: Ref<HTMLButtonElement>,
	) => {
		const { formatMessage } = useIntl();
		const { colorMode } = useThemeObserver();

		return (
			<Box
				xcss={isExpandable ? atlaskitCssStyles.boxStyleNoHovers : atlaskitCssStyles.boxStyles}
				data-spotlight-target="rovo-ai-button"
			>
				<Pressable
					testId="platform-ai-button"
					xcss={atlaskitCssStyles.pressableStyles}
					onClick={onClick}
					isDisabled={isDisabled}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onFocus={onMouseEnter}
					onBlur={onMouseLeave}
					ref={ref}
					interactionName="rovo-button-click"
				>
					<VisuallyHidden>{formatMessage(messages.rovoButton)}</VisuallyHidden>
					<Box xcss={atlaskitCssStyles.logoWrapper}>
						{proximityAnimationKey !== undefined ? (
							// `key` on the call site is the idiomatic React way to trigger
							// the SMIL animation replay: changing the key remounts the SVG,
							// browsers restart SMIL on insertion. RovoAnimatedLogo doesn't
							// need to know about this mechanism.
							<RovoAnimatedLogo
								key={proximityAnimationKey}
								colorMode={colorMode === 'dark' ? 'dark' : 'light'}
							/>
						) : (
							<RovoColorLogo colorMode={colorMode === 'dark' ? 'dark' : 'light'} />
						)}
					</Box>
				</Pressable>
				{/*
				 * Awaiting-input supersedes the plain unread dot: when chats need the
				 * user's input we show the blue information icon instead of the dot.
				 * (TREX-1891 rule 1)
				 */}
				{isAwaitingInput ? (
					<Box
						xcss={atlaskitCssStyles.awaitingInputIcon}
						testId="ai-button-awaiting-input-dot"
						aria-hidden="true"
					>
						<StatusInformationIcon
							label=""
							color={token('color.icon.information')}
							size="small"
							testId="ai-button-awaiting-input-icon"
						/>
					</Box>
				) : (
					hasUnread && (
						<Box
							xcss={atlaskitCssStyles.unreadDot}
							testId="ai-button-unread-dot"
							aria-hidden="true"
						/>
					)
				)}
			</Box>
		);
	},
);
